import { z } from "zod";
import { LINE_KINDS, PURCHASE_KINDS, VAT_RATES } from "./alicuotas.js";
import { isValidCuit, normalizeCuit } from "./cuit.js";
/** Request/response contracts of the fiscal API (plan 100). Messages are user-facing Spanish. */
export const VAT_CONDITIONS = [
    "responsable_inscripto",
    "monotributo",
    "exento",
    "consumidor_final",
    "no_categorizado",
];
export const VAT_CONDITION_LABELS = {
    responsable_inscripto: "Responsable inscripto",
    monotributo: "Monotributo",
    exento: "Exento",
    consumidor_final: "Consumidor final",
    no_categorizado: "No categorizado",
};
export const PERIOD_STATUSES = ["abierto", "en_revision", "cerrado", "presentado"];
export const PERIOD_STATUS_LABELS = {
    abierto: "Abierto",
    en_revision: "En revisión",
    cerrado: "Cerrado",
    presentado: "Presentado",
};
export const DIRECTIONS = ["compra", "venta"];
const decimal = z.union([z.string(), z.number()]).transform((v) => String(v).trim());
const decimalAmount = decimal.refine((v) => /^\d+(\.\d{1,2})?$/.test(v), {
    message: "El importe tiene que ser un número positivo con hasta dos decimales (ej. 1234.56).",
});
export const cuitSchema = z
    .string()
    .transform(normalizeCuit)
    .refine(isValidCuit, { message: "El CUIT no es válido: revisá el dígito verificador." });
export const companyCreateSchema = z.object({
    cuit: cuitSchema,
    legalName: z.string().trim().min(1, "La razón social es obligatoria.").max(200),
    vatCondition: z.enum(["responsable_inscripto", "monotributo", "exento"], {
        message: "Elegí la condición frente al IVA.",
    }),
    fiscalYearClosingMonth: z.number().int().min(1).max(12).default(12),
});
export const yearMonthSchema = z.object({
    year: z.number().int().min(2000).max(2100),
    month: z.number().int().min(1).max(12),
});
export const voucherLineInputSchema = z.object({
    kind: z.enum(LINE_KINDS),
    rate: z.enum(VAT_RATES).optional(),
    net: decimalAmount.default("0"),
    tax: decimalAmount.default("0"),
    /** IIBB perceptions: Convenio Multilateral code (902 Buenos Aires). */
    jurisdiction: z.number().int().min(901).max(924).optional(),
});
export const voucherInputSchema = z.object({
    direction: z.enum(DIRECTIONS),
    typeCode: z.number().int(),
    issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "La fecha tiene que ser AAAA-MM-DD."),
    /** Defaults to the issue month. A later month books it there (diferir compras). */
    period: yearMonthSchema.optional(),
    pointOfSale: z.number().int(),
    number: z.number().int(),
    counterpart: z.object({
        docType: z.union([z.literal(80), z.literal(86), z.literal(96), z.literal(99)]),
        docNumber: z.string().trim(),
        name: z.string().trim().min(1, "Falta el nombre de la contraparte.").max(200),
    }),
    currency: z.string().trim().toUpperCase().default("ARS"),
    exchangeRate: decimal.default("1"),
    total: decimalAmount,
    cae: z.string().trim().max(20).optional(),
    /** Compras only: what was bought (plan 016). */
    purchaseKind: z.enum(PURCHASE_KINDS).optional(),
    lines: z.array(voucherLineInputSchema).min(1, "Cargá al menos una línea de importes."),
});
export const reasonSchema = z.object({
    reason: z.string().trim().min(5, "Contá en pocas palabras por qué (mínimo 5 caracteres)."),
});
/** Moving needs no reason (Maxi, 2026-10-21): a blank one is recorded as none. */
export const voucherMoveSchema = yearMonthSchema.extend({
    reason: z
        .string()
        .trim()
        .optional()
        .transform((reason) => reason || undefined),
});
/** Most compras one bulk move takes: a month of a big client, with room to spare. */
export const VOUCHER_BULK_MOVE_MAX = 500;
/**
 * Several compras to one period at once, from the Registro de compras (Maxi, 2026-10-21).
 * `dryRun` only says which can go and which cannot, and why; the move is all or nothing.
 */
export const voucherBulkMoveSchema = voucherMoveSchema.extend({
    voucherIds: z
        .array(z.string().uuid())
        .min(1, "Elegí al menos un comprobante.")
        .max(VOUCHER_BULK_MOVE_MAX, `Se mueven hasta ${VOUCHER_BULK_MOVE_MAX} comprobantes por vez.`),
    dryRun: z.boolean().default(false),
});
/**
 * Voiding or reactivating several vouchers, all or none (plan 024: nothing is deleted). The
 * preview (`dryRun`) returns a token of the state it saw; the confirmation needs it, so a late
 * retry or a list that changed in between does nothing.
 */
export const voucherBulkVoidSchema = z
    .object({
    voucherIds: z
        .array(z.string().uuid())
        .min(1, "Elegí al menos un comprobante.")
        .max(VOUCHER_BULK_MOVE_MAX, `Se anulan hasta ${VOUCHER_BULK_MOVE_MAX} comprobantes por vez.`),
    // The preview needs no reason yet; the change always does.
    reason: z.string().trim().default(""),
    dryRun: z.boolean().default(false),
    token: z.string().optional(),
})
    .superRefine((body, ctx) => {
    if (body.dryRun)
        return;
    if (body.reason.length < 5) {
        ctx.addIssue({
            code: "custom",
            path: ["reason"],
            message: "Contá en pocas palabras por qué (mínimo 5 caracteres).",
        });
    }
    if (!body.token) {
        ctx.addIssue({
            code: "custom",
            path: ["token"],
            message: "Falta confirmar la vista previa: volvé a revisar la lista.",
        });
    }
});
/** At most this many vouchers come back from a search by issue date; more are flagged, not cut silently. */
export const VOUCHER_SEARCH_MAX = 500;
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "La fecha tiene que ser AAAA-MM-DD.")
    .refine(
// From 1900: PostgreSQL has no year zero (Codex 024 round 3).
(s) => s >= "1900-01-01" &&
    !Number.isNaN(Date.parse(`${s}T00:00:00Z`)) &&
    new Date(`${s}T00:00:00Z`).toISOString().startsWith(s), "Esa fecha no existe.");
/**
 * Vouchers of a company by issue date, in every period (Mathi, 2026-10-22: the Registro de
 * compras searches by the voucher's date, not only by the period it was taken in).
 */
export const voucherSearchSchema = z
    .object({
    direction: z.enum(["compra", "venta"]).optional(),
    issuedFrom: isoDate,
    issuedTo: isoDate,
    status: z.enum(["vigentes", "anulados", "todos"]).default("vigentes"),
})
    .refine((q) => q.issuedFrom <= q.issuedTo, {
    message: "La fecha «desde» no puede ser posterior a «hasta».",
    path: ["issuedTo"],
});
export const periodTransitionSchema = z.object({
    to: z.enum(PERIOD_STATUSES),
    reason: z.string().trim().optional(),
});
/**
 * The Caja's manual inputs stay below 10 billion pesos each (under 10^15 cents), so their sum with
 * the period's IVA never leaves the range where integer cents are exact (Codex 009 round 1).
 */
const cajaInput = decimalAmount.refine((v) => (v.split(".")[0] ?? "").replace(/^0+/, "").length <= 13, {
    message: "El importe es demasiado grande: el máximo es 9.999.999.999.999,99.",
});
export const vatPositionRequestSchema = z.object({
    saldoTecnicoAnterior: cajaInput,
    saldoLibreDisponibilidadAnterior: cajaInput,
    /** IVA retenciones suffered in the period; absent means none (clients before Caja v2). */
    retenciones: cajaInput.optional(),
});
export const counterpartUpdateSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    vatCondition: z.enum(VAT_CONDITIONS).nullable().optional(),
    status: z.literal("active").optional(),
});
/** Adds a client or supplier to a company's list (the studio record is created if it is new). */
export const companyCounterpartAddSchema = z.object({
    docType: z.union([z.literal(80), z.literal(86), z.literal(96), z.literal(99)]),
    docNumber: z.string().trim().min(1, "Falta el número de documento."),
    name: z.string().trim().min(1, "Falta el nombre.").max(200),
    vatCondition: z.enum(VAT_CONDITIONS).nullable().optional(),
    role: z.enum(["cliente", "proveedor"], { message: "Elegí si es cliente o proveedor." }),
});
//# sourceMappingURL=dto.js.map